import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, ProgressBar, Alert, Card, Row, Col, Badge } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import VodService from '../../../services/VodService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const VODUploader = () => {
  const [directories, setDirectories] = useState([]);
  const [selectedDirectory, setSelectedDirectory] = useState('');
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [showDirectoryModal, setShowDirectoryModal] = useState(false);
  const [newDirectoryName, setNewDirectoryName] = useState('');

  useEffect(() => {
    fetchDirectories();
  }, []);

  const fetchDirectories = async () => {
    try {
      const response = await VodService.getAllDirectories();
      if (response.success) {
        setDirectories(response.directories || []);
        if (response.directories?.length > 0 && !selectedDirectory) {
          setSelectedDirectory(response.directories[0].id.toString());
        }
      }
    } catch (error) {
      toast.error('Failed to fetch directories');
      console.error(error);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const newUploads = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      progress: 0,
      status: 'pending',
      title: title || file.name.replace(/\.[^/.]+$/, ""),
      description: description
    }));
    setUploads(prev => [...prev, ...newUploads]);
  }, [title, description]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
      'audio/*': ['.mp3', '.aac', '.wav']
    },
    multiple: true
  });

  const handleUpload = async () => {
    if (uploads.length === 0) {
      toast.warning('Please select files to upload');
      return;
    }

    if (!selectedDirectory) {
      toast.warning('Please select a directory');
      return;
    }

    setUploading(true);

    for (const upload of uploads) {
      if (upload.status === 'completed') continue;

      const formData = new FormData();
      formData.append('file', upload.file);
      formData.append('title', upload.title);
      formData.append('description', upload.description);
      formData.append('directory_id', selectedDirectory);

      try {
        setUploads(prev => prev.map(u => 
          u.id === upload.id ? { ...u, status: 'uploading' } : u
        ));

        await VodService.uploadAsset(formData, (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploads(prev => prev.map(u => 
            u.id === upload.id ? { ...u, progress } : u
          ));
        });

        setUploads(prev => prev.map(u => 
          u.id === upload.id ? { ...u, status: 'completed', progress: 100 } : u
        ));

        toast.success(`Uploaded: ${upload.title}`);
      } catch (error) {
        setUploads(prev => prev.map(u => 
          u.id === upload.id ? { ...u, status: 'error' } : u
        ));
        toast.error(`Failed to upload: ${upload.title}`);
        console.error(error);
      }
    }

    setUploading(false);
  };

  const removeUpload = (id) => {
    setUploads(prev => prev.filter(u => u.id !== id));
  };

  const createDirectory = async () => {
    if (!newDirectoryName.trim()) {
      toast.warning('Please enter a directory name');
      return;
    }

    try {
      const response = await VodService.createDirectory({
        name: newDirectoryName,
        description: 'Auto-created from uploader'
      });
      if (response.success) {
        toast.success('Directory created');
        setShowDirectoryModal(false);
        setNewDirectoryName('');
        fetchDirectories();
        setSelectedDirectory(response.directory.id.toString());
      }
    } catch (error) {
      toast.error('Failed to create directory');
      console.error(error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="vod-uploader">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>VOD Uploader</h2>
        <Button variant="outline-primary" onClick={() => setShowDirectoryModal(true)}>
          <i className="flaticon-plus mr-2"></i> New Directory
        </Button>
      </div>

      <Alert variant="info">
        <i className="flaticon-information mr-2"></i>
        Upload video and audio files to your media directories. Supported formats: MP4, MOV, AVI, MKV, WebM, MP3, AAC, WAV
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Select Directory *</Form.Label>
                <Form.Select 
                  value={selectedDirectory} 
                  onChange={(e) => setSelectedDirectory(e.target.value)}
                >
                  <option value="">Choose a directory...</option>
                  {directories.map(dir => (
                    <option key={dir.id} value={dir.id}>{dir.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Default Title (optional)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Default title for all uploads"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Default Description (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Default description for all uploads"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? 'active' : ''}`}
            style={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa'
            }}
          >
            <input {...getInputProps()} />
            <i className="flaticon-upload" style={{ fontSize: '48px', color: '#666' }}></i>
            <p className="mt-3 mb-0">
              {isDragActive ? (
                <span className="text-primary">Drop the files here...</span>
              ) : (
                <span>Drag & drop files here, or click to select files</span>
              )}
            </p>
            <p className="text-muted small mt-2">
              Maximum file size: 10GB per file
            </p>
          </div>
        </Card.Body>
      </Card>

      {uploads.length > 0 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Upload Queue ({uploads.length} files)</h5>
          </Card.Header>
          <Card.Body>
            {uploads.map((upload) => (
              <div key={upload.id} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <strong>{upload.title}</strong>
                    <p className="mb-0 text-muted small">
                      {upload.file.name} • {formatFileSize(upload.file.size)}
                    </p>
                  </div>
                  <div>
                    {upload.status === 'pending' && (
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => removeUpload(upload.id)}
                      >
                        <i className="flaticon-delete"></i>
                      </Button>
                    )}
                    {upload.status === 'completed' && (
                      <Badge variant="success">Completed</Badge>
                    )}
                    {upload.status === 'error' && (
                      <Badge variant="danger">Error</Badge>
                    )}
                    {upload.status === 'uploading' && (
                      <Badge variant="primary">Uploading...</Badge>
                    )}
                  </div>
                </div>
                
                {upload.status === 'uploading' && (
                  <ProgressBar 
                    now={upload.progress} 
                    label={`${upload.progress}%`}
                    animated
                  />
                )}
                
                {upload.status === 'completed' && (
                  <ProgressBar now={100} variant="success" />
                )}
                
                {upload.status === 'error' && (
                  <ProgressBar now={100} variant="danger" />
                )}
              </div>
            ))}

            <div className="d-flex justify-content-between mt-3">
              <Button 
                variant="outline-secondary" 
                onClick={() => setUploads([])}
                disabled={uploading}
              >
                Clear All
              </Button>
              <Button 
                variant="primary" 
                onClick={handleUpload}
                disabled={uploading || uploads.every(u => u.status === 'completed')}
              >
                {uploading ? 'Uploading...' : `Upload ${uploads.filter(u => u.status !== 'completed').length} Files`}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Create Directory Modal */}
      <Modal show={showDirectoryModal} onHide={() => setShowDirectoryModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Directory</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Directory Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter directory name"
              value={newDirectoryName}
              onChange={(e) => setNewDirectoryName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDirectoryModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={createDirectory}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VODUploader;
